"use client";

import { useState, useRef, useEffect } from "react";
import { FolderOpen, LayoutGrid, Plus, X, Check } from "lucide-react";
import { useAppFolders, useAppFolderMutations, type AppFolder } from "@/lib/hooks/useAppFolders";
import { getIconByName } from "./ItemEditPopover";
import { fallbackFolderIcon } from "@/lib/app-launcher/folder-icons";

interface FolderStripProps {
  selectedFolderId: string | null;
  onSelect: (id: string | null) => void;
  /** When true, sidebar is on the right side of the screen (RTL primary) */
  onRight: boolean;
  labels: {
    appFolders: string;
    allApps: string;
    newAppFolder: string;
    appFolderNamePlaceholder: string;
    createFirstFolder: string;
  };
}

const DEFAULT_GRADIENT: [string, string] = ["#475569", "#64748b"];

function gradientFor(folder: AppFolder): [string, string] {
  if (folder.color && folder.color.includes(",")) {
    const [a, b] = folder.color.split(",").map((s) => s.trim());
    if (a && b) return [a, b];
  }
  if (folder.color) return [folder.color, folder.color];
  return DEFAULT_GRADIENT;
}

export function FolderStrip({ selectedFolderId, onSelect, onRight, labels }: FolderStripProps) {
  const { folders, isLoading } = useAppFolders();
  const { createFolder } = useAppFolderMutations();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (creating) inputRef.current?.focus();
  }, [creating]);

  async function handleCreate() {
    const trimmed = newName.trim();
    if (!trimmed) {
      setCreating(false);
      return;
    }
    try {
      const id = await createFolder.mutateAsync({ name: trimmed });
      onSelect(id);
    } catch {
      // ignore — most likely UNIQUE conflict; UI will refetch and the existing folder shows up
    }
    setNewName("");
    setCreating(false);
  }

  return (
    <div className="px-1.5 pt-1.5 pb-0.5">
      <div className="flex items-center gap-1 px-1 mb-1.5">
        <FolderOpen className="h-2.5 w-2.5 text-slate-600" />
        <span className="text-[9px] text-slate-600 font-medium truncate">{labels.appFolders}</span>
      </div>

      <div className="flex flex-col items-center gap-1.5">
        {/* All apps (clear filter) */}
        <button
          type="button"
          onClick={() => onSelect(null)}
          title={labels.allApps}
          className={`group relative flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] transition-all duration-200 ${
            selectedFolderId === null
              ? "bg-slate-700/60 text-slate-200 ring-1 ring-slate-500"
              : "text-slate-500 hover:text-slate-300 hover:bg-slate-800/60"
          }`}
        >
          <LayoutGrid className="h-3.5 w-3.5" />
          <span
            className={`absolute ${
              onRight ? "right-full mr-2" : "left-full ml-2"
            } rounded-md bg-slate-800 border border-slate-700 px-2 py-1 text-[10px] text-slate-200 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50`}
          >
            {labels.allApps}
          </span>
        </button>

        {/* Folder buttons */}
        {!isLoading && folders.map((folder) => {
          const isActive = selectedFolderId === folder.id;
          const [from, to] = gradientFor(folder);
          // Legacy folders may have null icon. Fall back to a hashed
          // pick from FOLDER_ICON_POOL so each existing folder still
          // looks distinct (Sprint C.3 follow-up).
          const iconName = folder.icon ?? fallbackFolderIcon(folder.id);
          const Icon = getIconByName(iconName) ?? FolderOpen;
          return (
            <button
              key={folder.id}
              type="button"
              onClick={() => onSelect(isActive ? null : folder.id)}
              title={folder.name}
              className={`group relative flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] transition-all duration-200 ${
                isActive ? "scale-110" : "opacity-75 hover:opacity-100 hover:scale-105"
              }`}
              style={{
                background: `linear-gradient(135deg, ${from}, ${to})`,
                boxShadow: isActive
                  ? `0 4px 12px ${from}50, 0 0 0 2px rgba(255,255,255,0.18), inset 0 1px 0 rgba(255,255,255,0.2)`
                  : `0 2px 6px ${from}20`,
              }}
            >
              <div
                className="pointer-events-none absolute inset-0 rounded-[10px]"
                style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 45%)" }}
              />
              <Icon className="relative z-10 h-4 w-4 text-white drop-shadow-sm" />
              {isActive && (
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-1 w-3 rounded-full"
                  style={{ background: `linear-gradient(90deg, ${from}, ${to})`, boxShadow: `0 0 6px ${from}60` }}
                />
              )}
              <span
                className={`absolute ${
                  onRight ? "right-full mr-2" : "left-full ml-2"
                } rounded-md px-2 py-1 text-[10px] font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 shadow-lg`}
                style={{ background: `linear-gradient(135deg, ${from}ee, ${to}ee)`, backdropFilter: "blur(8px)" }}
              >
                {folder.name}
              </span>
            </button>
          );
        })}

        {/* Add folder button or inline form */}
        {!creating ? (
          <button
            type="button"
            onClick={() => setCreating(true)}
            title={folders.length === 0 ? labels.createFirstFolder : labels.newAppFolder}
            className="group relative flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] border border-dashed border-slate-700 text-slate-600 hover:text-slate-300 hover:border-slate-500 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            <span
              className={`absolute ${
                onRight ? "right-full mr-2" : "left-full ml-2"
              } rounded-md bg-slate-800 border border-slate-700 px-2 py-1 text-[10px] text-slate-200 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50`}
            >
              {folders.length === 0 ? labels.createFirstFolder : labels.newAppFolder}
            </span>
          </button>
        ) : (
          <div className="flex flex-col gap-0.5 w-full">
            <input
              ref={inputRef}
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCreate();
                } else if (e.key === "Escape") {
                  setNewName("");
                  setCreating(false);
                }
              }}
              placeholder={labels.appFolderNamePlaceholder}
              className="w-full rounded-md bg-slate-800 border border-slate-600 text-[10px] text-slate-100 placeholder-slate-500 px-1.5 py-1 outline-none focus:border-slate-400"
            />
            <div className="flex items-center justify-between gap-0.5">
              <button
                type="button"
                onClick={() => { setNewName(""); setCreating(false); }}
                className="rounded p-0.5 text-slate-500 hover:text-slate-300"
              >
                <X className="h-2.5 w-2.5" />
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={!newName.trim() || createFolder.isPending}
                className="rounded p-0.5 text-emerald-400 hover:text-emerald-300 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Check className="h-2.5 w-2.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
